import type { ReactNode } from 'react';

export type SectionColor = 'blue' | 'purple' | 'amber' | 'green' | 'navy';

interface DashboardSectionProps {
  /** Anchor id — used by the scrollspy nav (must match a DashboardSectionNav entry). */
  id: string;
  /** Accent colour for the left border, header tint and icon chip. */
  color: SectionColor;
  /** Emoji / glyph rendered in the coloured icon chip. */
  icon: ReactNode;
  /** Section heading. */
  title: string;
  /** Compact summary shown in the (always-visible) header band — typically derived numbers. */
  summary?: ReactNode;
  /** Collapsed/expanded state — controlled by the parent (DashboardTab). */
  open: boolean;
  /** Toggle handler for the header click / caret. */
  onToggle: () => void;
  /** Section body (an existing dashboard section), rendered untouched when open. */
  children: ReactNode;
}

/**
 * Static per-colour class maps. Tailwind v4 can only see class names that appear
 * literally in source, so every variant is spelled out (no template-string
 * interpolation of the colour token). Mirrors the mockup's `section[data-c=…]`
 * styling: tinted header band (subtle left→right gradient), coloured left border
 * and an icon chip in the same hue.
 */
const COLOR: Record<
  SectionColor,
  { border: string; head: string; chip: string; caretDot: string }
> = {
  blue: {
    border: 'border-l-td-blue',
    head: 'bg-gradient-to-r from-td-blue/[0.06] to-transparent',
    chip: 'bg-td-blue/10 text-td-blue',
    caretDot: 'bg-td-blue',
  },
  purple: {
    border: 'border-l-td-purple',
    head: 'bg-gradient-to-r from-td-purple/[0.06] to-transparent',
    chip: 'bg-td-purple/10 text-td-purple',
    caretDot: 'bg-td-purple',
  },
  amber: {
    border: 'border-l-td-orange',
    head: 'bg-gradient-to-r from-td-orange/[0.07] to-transparent',
    chip: 'bg-td-orange/10 text-td-orange',
    caretDot: 'bg-td-orange',
  },
  green: {
    border: 'border-l-td-green',
    head: 'bg-gradient-to-r from-td-green/[0.07] to-transparent',
    chip: 'bg-td-green/10 text-td-green',
    caretDot: 'bg-td-green',
  },
  navy: {
    border: 'border-l-td-navy',
    head: 'bg-gradient-to-r from-td-navy/[0.05] to-transparent',
    chip: 'bg-td-navy/10 text-td-navy',
    caretDot: 'bg-td-navy',
  },
};

/**
 * Coloured collapsible shell that wraps each dashboard section.
 *
 * Pure chrome: a tinted header band (icon chip + title + summary + caret) over a
 * coloured left border, with a collapsible body. The section's real content is
 * passed as `children` and rendered untouched when expanded — this component
 * never alters or summarises it (the summary line is additive, supplied by the
 * caller). `scroll-mt` gives the scrollspy a comfortable offset below the sticky
 * nav.
 */
export function DashboardSection({
  id,
  color,
  icon,
  title,
  summary,
  open,
  onToggle,
  children,
}: DashboardSectionProps) {
  const c = COLOR[color];
  const bodyId = `${id}-body`;

  return (
    <section
      id={id}
      data-color={color}
      className="scroll-mt-20 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className={`flex w-full items-center gap-3 border-l-4 ${c.border} ${c.head} px-4 py-3 text-left`}
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-lg ${c.chip}`}
          aria-hidden="true"
        >
          {icon}
        </span>
        <h2 className="shrink-0 text-base font-extrabold text-td-navy">{title}</h2>
        {summary != null && (
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-td-muted">
            {summary}
          </span>
        )}
        <svg
          className={`ml-auto h-4 w-4 shrink-0 text-td-muted transition-transform ${open ? '' : '-rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {/* Kept mounted when collapsed (hidden, not unmounted) so each section's data
          still loads on page open — the status line depends on the Vordering section's
          fetch, and sections shouldn't re-fetch on every expand/collapse. */}
      <div
        id={bodyId}
        hidden={!open}
        className="border-t border-gray-200 px-4 pb-4 pt-3 sm:px-5"
      >
        {children}
      </div>
    </section>
  );
}
